// Connect playbooks and ReConnect (docs/SYSTEM_MAP.md; migration 20261107090000; engine/connect.py). The settings as
// the database cleans them (crm__connect_settings), the words the screens use, and the line that says what happens
// next for a firm in ReConnect. Read only: changes to the playbook go through its approval (0.5).
import type { JsonObject } from "./types";

export type Flavour = "attract" | "find" | "network";
export type FirstAsk = "book" | "quote" | "call" | "coffee";
export type OfferAfter = "first_reply" | "meeting" | "relationship";
export type SellingMethod = "call" | "instant_price" | "qa_quote" | "proposal" | "site_visit" | "tender_response";
export type OfferKind = "discount" | "smaller_first_job" | "payment_terms" | "trial";
export type Approval = "always" | "never" | "above";

export const FLAVOURS: Flavour[] = ["attract", "find", "network"];
export const SELLING: SellingMethod[] = ["call", "instant_price", "qa_quote", "proposal", "site_visit", "tender_response"];
export const OFFER_KINDS: OfferKind[] = ["discount", "smaller_first_job", "payment_terms", "trial"];
export const LOSS_CATEGORIES = ["price", "scope", "timing", "competitor", "trust", "other"] as const;
export type LossCategory = (typeof LOSS_CATEGORIES)[number];

export const FLAVOUR_LABEL: Record<Flavour, string> = { attract: "Attract", find: "Find", network: "Network" };
export const FLAVOUR_HINT: Record<Flavour, string> = {
  attract: "They came to you: reply fast and make the next step easy.",
  find: "You went to them: earn the meeting first, offer after it.",
  network: "Introduced or met: the relationship before the offer.",
};
export const FIRST_ASK_LABEL: Record<FirstAsk, string> = { book: "Book a call", quote: "A quote", call: "A short call", coffee: "A coffee or informal chat" };
export const OFFER_AFTER_LABEL: Record<OfferAfter, string> = { first_reply: "From the first reply", meeting: "After a meeting", relationship: "Once the relationship is there" };
export const SELLING_LABEL: Record<SellingMethod, string> = {
  call: "Book a call",
  instant_price: "Instant price",
  qa_quote: "Q&A quote",
  proposal: "Proposal",
  site_visit: "Site visit or survey",
  tender_response: "Tender or RFP response",
};
/** What the engine does after a meeting for each method (engine/connect.py start_quote). */
export const SELLING_AUTOMATION: Record<SellingMethod, string> = {
  call: "Nothing more: the call was the method",
  instant_price: "A task for you: no price list in Ogma yet (roadmap 10)",
  qa_quote: "A task for you: the Q&A quote engine is not built yet (roadmap 13)",
  proposal: "A task for you to send the proposal, which waits for your approval",
  site_visit: "A task for you to book the visit",
  tender_response: "A task for you to prepare the response",
};
export const OFFER_KIND_LABEL: Record<OfferKind, string> = { discount: "A discount", smaller_first_job: "A smaller first job", payment_terms: "Payment terms", trial: "A trial" };
export const LOSS_LABEL: Record<LossCategory, string> = { price: "Price", scope: "Scope", timing: "Timing", competitor: "Competitor", trust: "Trust", other: "Other" };

export interface FlavourSettings {
  reply_within_hours: number;
  first_ask: FirstAsk;
  offer_after: OfferAfter;
  touches_before_offer: number;
  selling: SellingMethod[];
}

export interface SaveOfferSettings {
  enabled: boolean;
  kinds: OfferKind[];
  max_discount_pct: number;
  approval: Approval;
  approve_above_pct: number;
  decide_after_days: number;
}

export interface ReconnectSettings {
  min_gap_days: number;
  return_score: number;
  offers: { enabled: boolean; after_days: number; max_per_quarter: number; max_discount_pct: number; kinds: OfferKind[]; match: Record<LossCategory, OfferKind | "none"> };
}

export interface ConnectSettings {
  flavours: Record<Flavour, FlavourSettings>;
  selling: SellingMethod[];
  save_offer: SaveOfferSettings;
  reconnect: ReconnectSettings;
}

const DEFAULT_FLAVOURS: Record<Flavour, Omit<FlavourSettings, "selling">> = {
  attract: { reply_within_hours: 1, first_ask: "book", offer_after: "first_reply", touches_before_offer: 0 },
  find: { reply_within_hours: 8, first_ask: "call", offer_after: "meeting", touches_before_offer: 1 },
  network: { reply_within_hours: 24, first_ask: "coffee", offer_after: "relationship", touches_before_offer: 3 },
};
const DEFAULT_MATCH: Record<LossCategory, OfferKind | "none"> = { price: "discount", scope: "smaller_first_job", timing: "payment_terms", competitor: "trial", trust: "trial", other: "none" };

const obj = (v: unknown): JsonObject => (v && typeof v === "object" && !Array.isArray(v) ? (v as JsonObject) : {});
const num = (v: unknown, lo: number, hi: number, d: number) => (typeof v === "number" && Number.isFinite(v) && v >= lo && v <= hi ? v : d);
function known<T extends string>(v: unknown, allowed: readonly T[]): T[] {
  const out: T[] = [];
  for (const x of Array.isArray(v) ? v : []) if ((allowed as readonly unknown[]).includes(x) && !out.includes(x as T)) out.push(x as T);
  return out;
}
const oneOf = <T extends string>(v: unknown, allowed: readonly T[], d: T): T => ((allowed as readonly unknown[]).includes(v) ? (v as T) : d);

/** The business's Connect settings from its approved playbook, cleaned as the database does (crm__connect_settings). */
export function connectSettings(config: JsonObject | null | undefined): ConnectSettings {
  const c = obj(config?.connect);
  const selling = known(c.selling, SELLING);
  const sell = selling.length ? selling : (["call"] as SellingMethod[]);
  const flavours = {} as Record<Flavour, FlavourSettings>;
  for (const f of FLAVOURS) {
    const g = obj(obj(c.flavours)[f]);
    const d = DEFAULT_FLAVOURS[f];
    const own = known(g.selling, SELLING);
    flavours[f] = {
      reply_within_hours: num(g.reply_within_hours, 0.25, 720, d.reply_within_hours),
      first_ask: oneOf(g.first_ask, ["book", "quote", "call", "coffee"] as const, d.first_ask),
      offer_after: oneOf(g.offer_after, ["first_reply", "meeting", "relationship"] as const, d.offer_after),
      touches_before_offer: Math.trunc(num(g.touches_before_offer, 0, 20, d.touches_before_offer)),
      selling: own.length ? own : sell,
    };
  }
  const so = obj(c.save_offer);
  const kinds = known(so.kinds, OFFER_KINDS);
  const save: SaveOfferSettings = {
    enabled: so.enabled === true,
    kinds: kinds.length ? kinds : [...OFFER_KINDS],
    max_discount_pct: num(so.max_discount_pct, 0, 100, 10),
    approval: oneOf(so.approval, ["always", "never", "above"] as const, "always"),
    approve_above_pct: num(so.approve_above_pct, 0, 100, 5),
    decide_after_days: Math.trunc(num(so.decide_after_days, 1, 60, 7)),
  };
  const rc = obj(config?.reconnect);
  const ro = obj(rc.offers);
  const m = obj(ro.match);
  const match = {} as Record<LossCategory, OfferKind | "none">;
  for (const k of LOSS_CATEGORIES) match[k] = m[k] === "none" || (OFFER_KINDS as unknown[]).includes(m[k]) ? (m[k] as OfferKind | "none") : DEFAULT_MATCH[k];
  const rkinds = known(ro.kinds, OFFER_KINDS);
  return {
    flavours,
    selling: sell,
    save_offer: save,
    reconnect: {
      min_gap_days: Math.trunc(num(rc.min_gap_days, 0, 365, 30)),
      return_score: num(rc.return_score, 0, 1000, 60),
      offers: {
        enabled: ro.enabled === true,
        after_days: Math.trunc(num(ro.after_days, 0, 365, 30)),
        max_per_quarter: Math.trunc(num(ro.max_per_quarter, 0, 12, 1)),
        max_discount_pct: num(ro.max_discount_pct, 0, 100, save.max_discount_pct),
        kinds: rkinds.length ? rkinds : [...OFFER_KINDS],
        match,
      },
    },
  };
}

/** "1 hour", "8 hours", "1 day", "30 minutes". */
export function hoursText(h: number): string {
  if (h < 1) return `${Math.round(h * 60)} minutes`;
  if (h === 1) return "1 hour";
  if (h >= 24 && h % 24 === 0) return h === 24 ? "1 day" : `${h / 24} days`;
  return `${h} hours`;
}

/** The save offer's approval rule in words. */
export function approvalText(s: SaveOfferSettings): string {
  if (s.approval === "never") return "No approval for the offer itself (the email still goes through your message approvals)";
  if (s.approval === "above") return `You approve any discount over ${s.approve_above_pct}%`;
  return "You approve every save offer";
}

/** When an offer may be made, in words. */
export function offerRuleText(f: FlavourSettings): string {
  const base = OFFER_AFTER_LABEL[f.offer_after];
  return f.touches_before_offer > 0 ? `${base}, after ${f.touches_before_offer} ${f.touches_before_offer === 1 ? "conversation" : "conversations"}` : base;
}

/** crm_connect_playbook for one firm. */
export interface Playbook {
  organisation_id: string;
  organisation?: string;
  flavour: Flavour;
  flavour_label: string;
  /** True once the firm has entered Connect (the flavour is stored); false while it is only worked out. */
  stored?: boolean;
  reply_within_hours: number;
  first_ask: FirstAsk;
  offer_after: OfferAfter;
  touches_before_offer: number;
  selling: SellingMethod[];
  conversations: number;
  meetings_held: number;
  may_offer: boolean;
  offer_note: string;
  reply_due_by?: string;
  answered?: boolean;
  introducer?: { organisation?: string; person?: string };
  quiet_until?: string;
}

/** One row of crm_reconnect_board (crm_reconnect_status). */
export interface ReconnectStatus {
  organisation_id: string;
  organisation: string;
  stage: string;
  in_reconnect: boolean;
  flavour: Flavour;
  left_at?: string;
  left_reason?: string;
  reason_category?: LossCategory;
  deal_id?: string;
  last_contact_at?: string;
  next_contact_at?: string;
  quiet_until?: string;
  suppressed?: boolean;
  offer_kind?: OfferKind;
  offers_this_quarter: number;
  offer_cap: number;
  offer_allowed: boolean;
  offer_blocked?: string;
  next_offer_at?: string;
  offers_enabled?: boolean;
  intent_score?: number | null;
  return_score?: number;
  return_due?: "book" | "score";
}

/** One row of crm_connect_offers. */
export interface ConnectOffer {
  id: string;
  kind: "save" | "reconnect";
  offer_kind: OfferKind;
  discount_pct: number | null;
  terms: string;
  reason: string | null;
  reason_category: string | null;
  subject: string | null;
  body: string | null;
  status: "awaiting_approval" | "queued" | "rejected" | "accepted" | "declined" | "cancelled";
  needs_approval: boolean;
  decided_by: string | null;
  deal_id: string | null;
  organisation_id: string;
  outbox_status: string | null;
  created_at: string;
}

const day = (iso: string) => new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(iso));

/** What happens next for a firm in ReConnect, in one line (the Keep warm page). */
export function reconnectNext(s: ReconnectStatus, now: Date = new Date()): string {
  if (!s.in_reconnect) return s.stage === "closed" ? "Closed: not contacted again" : "Back in Connect";
  if (s.suppressed) return "Asked not to be contacted";
  if (s.return_due === "book") return "Back in Connect on the next run: they used a booking link";
  if (s.return_due === "score") return `Back in Connect on the next run: score ${Math.round(Number(s.intent_score ?? 0))}`;
  if (s.quiet_until) return `Tender quiet period until ${day(s.quiet_until)}`;
  if (s.offer_allowed && s.offer_kind) return `${OFFER_KIND_LABEL[s.offer_kind]} can be offered now`;
  const back = s.return_score !== undefined ? ` Back in Connect if they reply, book or their score reaches ${s.return_score}.` : "";
  if (!s.offers_enabled) return `Keep-in-touch notes only.${back}`;
  if (!s.offer_kind) return `No offer matches why they left.${back}`;
  if (s.next_offer_at && new Date(s.next_offer_at) > now) return `${OFFER_KIND_LABEL[s.offer_kind]} from ${day(s.next_offer_at)}.${back}`;
  return `${s.offer_blocked ? s.offer_blocked.charAt(0).toUpperCase() + s.offer_blocked.slice(1) : "No offer now"}.${back}`;
}

/** "10% off" or the kind, for a card. */
export function offerText(o: Pick<ConnectOffer, "offer_kind" | "discount_pct" | "terms">): string {
  return o.offer_kind === "discount" && o.discount_pct !== null ? `${o.discount_pct}% discount: ${o.terms}` : `${OFFER_KIND_LABEL[o.offer_kind]}: ${o.terms}`;
}
